import { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { toast } from 'sonner';
import {
  Mic, MicOff, Tractor, MapPin, Fuel, Phone, FileText, Check, X,
  Loader2, AlertTriangle, Trash2, Plus, Edit3, Clock
} from 'lucide-react';
import api from '@/lib/api';

const TYPE_CONFIG = {
  pointage: { icon: Clock, label: 'Pointage journée', color: 'bg-purple-100 text-purple-700 border-purple-300' },
  intervention: { icon: MapPin, label: 'Intervention', color: 'bg-green-100 text-green-700 border-green-300' },
  heures_materiel: { icon: Tractor, label: 'Heures matériel', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  consommation: { icon: Fuel, label: 'Consommation', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  demande_client: { icon: Phone, label: 'Demande client', color: 'bg-red-100 text-red-700 border-red-300' },
  note: { icon: FileText, label: 'Note', color: 'bg-gray-100 text-gray-700 border-gray-300' },
};

export default function AssistantTerrainPage() {
  const [recording, setRecording] = useState(false);
  const [continuousMode, setContinuousMode] = useState(false);
  const [chunkCount, setChunkCount] = useState(0);
  const wakeLockRef = useRef(null);
  const chunkTimerRef = useRef(null);
  const accumulatedTextRef = useRef('');
  const [processing, setProcessing] = useState(false);
  const [contexte, setContexte] = useState('eta'); // 'eta' ou 'exploitation'
  const [session, setSession] = useState(null);
  const [items, setItems] = useState([]); // items extraits éditables
  const [excludedItems, setExcludedItems] = useState(new Set());
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [recordTime, setRecordTime] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (chunkTimerRef.current) clearInterval(chunkTimerRef.current);
    if (wakeLockRef.current) wakeLockRef.current.release().catch(() => {});
  }, []);

  // Demande Wake Lock pour empêcher la mise en veille
  const acquireWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        wakeLockRef.current.addEventListener('release', () => {});
      }
    } catch (e) { /* ignore */ }
  };

  const releaseWakeLock = async () => {
    if (wakeLockRef.current) {
      try { await wakeLockRef.current.release(); } catch (e) { /* ignore */ }
      wakeLockRef.current = null;
    }
  };

  // Mode continu : envoie un chunk toutes les 60 secondes pour traitement
  const sendChunkContinuous = async () => {
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') return;
    if (chunksRef.current.length === 0) return;
    const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
    chunksRef.current = []; // Reset chunks pour le prochain cycle
    if (blob.size < 5000) return; // Skip si trop petit (< 5KB)

    const formData = new FormData();
    formData.append('file', blob, `chunk-${Date.now()}.webm`);
    try {
      const r = await api.post(`/assistant-terrain/transcribe-and-classify?contexte=${contexte}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      if (r.data.success && (r.data.items?.length || 0) > 0) {
        // Accumuler les items dans la session courante
        setItems(prev => [...prev, ...r.data.items]);
        setChunkCount(c => c + 1);
        if (!session) {
          setSession({
            id: r.data.session_id,
            transcription: r.data.transcription,
            resume: r.data.resume_global,
            questions: r.data.questions_globales || [],
          });
        } else {
          setSession(s => ({
            ...s,
            transcription: (s.transcription || '') + ' ' + (r.data.transcription || ''),
          }));
        }
      }
    } catch (e) {
      console.warn('Chunk continu failed:', e.message);
    }
  };

  const startRecording = async (continuous = false) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      accumulatedTextRef.current = '';
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = continuous ? () => {} : handleStop;
      mr.start(continuous ? 5000 : undefined); // chunks 5s en mode continu
      mediaRecorderRef.current = mr;
      setRecording(true);
      setContinuousMode(continuous);
      setRecordTime(0);
      setChunkCount(0);
      timerRef.current = setInterval(() => setRecordTime(t => t + 1), 1000);
      if (continuous) {
        // Wake lock + traitement chunks toutes les 60 sec
        acquireWakeLock();
        chunkTimerRef.current = setInterval(sendChunkContinuous, 60000);
        toast.success('🎙️ Mode dictée continue activé. Tu peux verrouiller l\'écran.');
      }
    } catch (e) {
      toast.error('Micro inaccessible: ' + e.message);
    }
  };

  const stopRecording = async () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop());
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (chunkTimerRef.current) { clearInterval(chunkTimerRef.current); chunkTimerRef.current = null; }
    await releaseWakeLock();
    setRecording(false);
    setContinuousMode(false);
    if (continuousMode) {
      // Envoyer le dernier chunk
      await sendChunkContinuous();
      toast.success(`Dictée continue terminée. ${chunkCount} segments traités.`);
    }
  };

  const handleStop = async () => {
    setProcessing(true);
    const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
    const formData = new FormData();
    formData.append('file', blob, 'enregistrement.webm');
    try {
      const url = `/assistant-terrain/transcribe-and-classify?contexte=${contexte}`;
      const r = await api.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      if (r.data.success) {
        setSession({
          id: r.data.session_id,
          transcription: r.data.transcription,
          resume: r.data.resume_global,
          questions: r.data.questions_globales || [],
        });
        setItems(r.data.items || []);
        setExcludedItems(new Set());
        if ((r.data.items || []).length === 0) {
          toast.warning('Aucune information extraite. Reformule.');
        } else {
          toast.success(`${r.data.items.length} élément(s) extrait(s)`);
        }
      } else {
        toast.error('Audio non reconnu');
      }
    } catch (e) {
      toast.error('Erreur: ' + (e.response?.data?.detail || e.message));
    } finally {
      setProcessing(false);
    }
  };

  const updateItemField = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, data: { ...it.data, [field]: value } } : it));
  };

  const toggleExclude = (idx) => {
    setExcludedItems(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const validateAll = async () => {
    if (!session) return;
    const itemsToValidate = items.filter((_, i) => !excludedItems.has(i));
    if (itemsToValidate.length === 0) {
      toast.info('Rien à valider');
      return;
    }
    try {
      const r = await api.post('/assistant-terrain/valider-session', {
        session_id: session.id,
        items_valides: itemsToValidate,
      });
      const e = r.data.enregistrements;
      const summary = Object.entries(e).filter(([_, v]) => v > 0).map(([k, v]) => `${v} ${k.replace('_', ' ')}`).join(', ');
      toast.success('✅ Enregistré: ' + (summary || '0 items'));
      // Reset
      setSession(null);
      setItems([]);
      setExcludedItems(new Set());
    } catch (e) {
      toast.error('Erreur validation: ' + (e.response?.data?.detail || e.message));
    }
  };

  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  return (
    <div className="space-y-6 p-6 max-w-5xl mx-auto" data-testid="page-assistant-terrain">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Mic className="w-8 h-8 text-green-600" />
          Assistant Terrain
        </h1>
        <p className="text-muted-foreground mt-1">
          Parle de ta journée. L'IA trie automatiquement : interventions, heures matériel, consommations, demandes clients.
        </p>
      </div>

      {/* Choix contexte */}
      <Card>
        <CardContent className="pt-4">
          <Label className="mb-2 block">Contexte</Label>
          <ToggleGroup type="single" value={contexte} onValueChange={(v) => v && setContexte(v)}>
            <ToggleGroupItem value="eta" data-testid="ctx-eta">🚜 ETA (interventions chez clients)</ToggleGroupItem>
            <ToggleGroupItem value="exploitation" data-testid="ctx-exploitation">🌾 Exploitation (sur ma ferme)</ToggleGroupItem>
          </ToggleGroup>
        </CardContent>
      </Card>

      {/* Bouton enregistrement */}
      <Card className={recording ? 'border-red-500 border-2' : ''}>
        <CardContent className="pt-6 text-center space-y-4">
          {!recording && !processing && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                size="lg"
                onClick={() => startRecording(false)}
                className="bg-green-600 hover:bg-green-700 text-white px-10 py-7 text-lg rounded-full"
                data-testid="btn-start-recording"
              >
                <Mic className="w-6 h-6 mr-3" /> Enregistrement court
              </Button>
              <Button
                size="lg"
                onClick={() => startRecording(true)}
                variant="outline"
                className="border-purple-500 text-purple-700 hover:bg-purple-50 px-10 py-7 text-lg rounded-full"
                data-testid="btn-start-continuous"
                title="Le micro reste ouvert toute la journée. Résiste au verrouillage écran. Idéal pour réunions, appels clients."
              >
                <Mic className="w-6 h-6 mr-3" /> Mode dictée continue
              </Button>
            </div>
          )}
          {recording && (
            <>
              <div className="flex items-center justify-center gap-2">
                <span className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></span>
                <span className="font-mono text-2xl">{fmt(recordTime)}</span>
                {continuousMode && (
                  <Badge className="bg-purple-600 ml-3" data-testid="badge-continuous">
                    🔁 Mode continu · {chunkCount} segments traités
                  </Badge>
                )}
              </div>
              <Button
                size="lg"
                onClick={stopRecording}
                variant="destructive"
                className="px-12 py-6 text-lg rounded-full"
                data-testid="btn-stop-recording"
              >
                <MicOff className="w-6 h-6 mr-3" /> Arrêter et analyser
              </Button>
              {continuousMode ? (
                <p className="text-sm text-purple-700 italic">
                  🔁 Le micro reste ouvert même écran verrouillé. Tu peux passer dans d'autres apps.
                  L'IA traite ta voix toutes les 60 sec et accumule les items.
                </p>
              ) : (
                <p className="text-sm text-muted-foreground italic">
                  💡 Exemple: "J'ai attaqué à 7h30, ce matin chez Domaine Martin, j'ai pulvérisé 12 hectares avec le tracteur John Deere et le pulvé 800L, Caloria à 15L/ha entre 8h et 10h. Pause à midi 1h. J'ai fini à 17h30, soit 9h totales dont 7h facturables au client. Plein de gasoil à la station Y."
                </p>
              )}
            </>
          )}
          {processing && (
            <div className="flex flex-col items-center gap-3 py-6" data-testid="state-processing">
              <Loader2 className="w-10 h-10 animate-spin text-green-600" />
              <p>Transcription et analyse IA en cours...</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Résultats */}
      {session && (
        <Card data-testid="card-session-result">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Analyse de ta journée</span>
              <Badge variant="outline" data-testid="badge-items-count">{items.length - excludedItems.size}/{items.length} items à valider</Badge>
            </CardTitle>
            <p className="text-sm text-muted-foreground italic">"{session.resume}"</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Transcription brute */}
            <details className="text-xs text-muted-foreground bg-gray-50 p-2 rounded">
              <summary className="cursor-pointer font-medium">Voir transcription brute</summary>
              <p className="mt-2 italic">"{session.transcription}"</p>
            </details>

            {/* Questions globales (clarifications) */}
            {session.questions && session.questions.length > 0 && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2 text-amber-700 font-medium">
                  <AlertTriangle className="w-4 h-4" />
                  L'IA a besoin de précisions
                </div>
                <ul className="space-y-1 text-sm text-amber-800 ml-6 list-disc">
                  {session.questions.map((q, i) => <li key={i}>{q}</li>)}
                </ul>
                <p className="text-xs text-muted-foreground">
                  Tu peux modifier les items ci-dessous ou refaire un enregistrement plus précis.
                </p>
              </div>
            )}

            {/* Items extraits */}
            <ScrollArea className="max-h-[600px]">
              <div className="space-y-3">
                {items.map((item, idx) => {
                  const cfg = TYPE_CONFIG[item.type] || TYPE_CONFIG.note;
                  const Icon = cfg.icon;
                  const excluded = excludedItems.has(idx);
                  return (
                    <Card key={idx} className={`${cfg.color} ${excluded ? 'opacity-40' : ''} border-2`} data-testid={`item-${item.type}-${idx}`}>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex items-center gap-2">
                            <Icon className="w-5 h-5" />
                            <span className="font-semibold">{cfg.label}</span>
                            {item.confiance && (
                              <Badge variant="outline" className="text-xs">
                                Confiance {Math.round(item.confiance * 100)}%
                              </Badge>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleExclude(idx)}
                            data-testid={`btn-toggle-${idx}`}
                          >
                            {excluded ? <Plus className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        </div>

                        {/* Champs éditables */}
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          {Object.entries(item.data || {}).map(([k, v]) => (
                            <div key={k} className="space-y-1">
                              <Label className="text-xs capitalize">{k.replace(/_/g, ' ')}</Label>
                              <Input
                                value={v ?? ''}
                                onChange={(e) => updateItemField(idx, k, e.target.value)}
                                disabled={excluded}
                                className="h-8 text-sm bg-white"
                                data-testid={`field-${idx}-${k}`}
                              />
                            </div>
                          ))}
                        </div>

                        {item.question_clarification && (
                          <div className="mt-2 text-xs italic text-amber-700 flex items-center gap-1">
                            <Edit3 className="w-3 h-3" /> {item.question_clarification}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}

                {items.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">
                    Aucun élément extrait. Réessaie avec plus de détails.
                  </p>
                )}
              </div>
            </ScrollArea>

            {/* Actions */}
            <div className="flex justify-end gap-3 sticky bottom-0 bg-background pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => { setSession(null); setItems([]); setExcludedItems(new Set()); }}
                data-testid="btn-cancel"
              >
                <X className="w-4 h-4 mr-2" />
                Annuler
              </Button>
              <Button
                onClick={validateAll}
                disabled={items.length === 0 || excludedItems.size === items.length}
                className="bg-green-600 hover:bg-green-700"
                data-testid="btn-validate-all"
              >
                <Check className="w-4 h-4 mr-2" />
                Tout valider et enregistrer
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
